import { NgFor, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterEvent,
  RouterLink,
  RouterOutlet
} from '@angular/router';
import { CobaltDividerComponent } from '@cae/cobalt-angular/components/divider';
import {
  CobaltIconComponent,
  cobaltIconFeedbackOutline,
  cobaltIconHelpCircleOutline,
  cobaltIconHomeOutline,
  cobaltIconHomeSolid,
  cobaltIconLibraryOutline,
  cobaltIconLibrarySolid,
  cobaltIconQrCodeOutline,
  cobaltIconQrCodeSolid,
  CobaltIconRegistry
} from '@cae/cobalt-angular/components/icon';
import { CobaltLayoutComponent } from '@cae/cobalt-angular/components/layout';
import { CobaltLoadingIndicatorCircularComponent } from '@cae/cobalt-angular/components/loading-indicator-circular';
import { CobaltModalService } from '@cae/cobalt-angular/components/modal';
import {
  CobaltSideNavigationComponent,
  CobaltSideNavigationItemComponent
} from '@cae/cobalt-angular/components/side-navigation';
import { Message } from '@core/models/message.model';
import { AnalyticService } from '@core/services/analytic/analytic.service';
import { HeapService } from '@core/services/heap/heap.service';
import { MonitorService } from '@core/services/monitor/monitor.service';
import { ConnectionService } from '@core/services/network-connection/network-connection.service';
import { PropertyBagService } from '@core/services/property-bag/property.bag.service';
import { QualtricsService } from '@core/services/qualtrics/qualtrics.service';
import { ServerErrorConnectorService } from '@core/services/server-error-connector/server-error-connector.service';
import { BannerComponent } from '@shared/components/banner/banner.component';
import { BannerService } from '@shared/components/banner/banner.service';
import { ToggleFeatureKeys } from '@shared/components/feature-toggle/enums/toggle-feature-keys.enum';
import { FeatureService } from '@shared/components/feature-toggle/services/feature.service';
import { HeaderComponent } from '@shared/components/header/header.component';
import { HeaderService } from '@shared/components/header/header.service';
import { LoadingCircularPanelComponent } from '@shared/components/loading-circular-panel/loading-circular-panel.component';
import { NetworkConnectionDialogComponent } from '@shared/components/network-connection-dialog/network-connection-dialog.component';
import { SessionExperationDialogComponent } from '@shared/components/session-experation-dialog/session-experation-dialog.component';
import { ProgramType } from '@shared/models/enums/program-type.enum';
import { TrainingEventsService } from '@training/services/training-events/training-events.service';
import { AuthB2CService } from 'common-web-core';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, interval, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

export const BLANK_ROUTE = '/';
export const AUTH_NAVIGATION_KEY = 'access_token';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
  standalone: true,
  imports: [
    BannerComponent,
    CobaltDividerComponent,
    CobaltIconComponent,
    CobaltLayoutComponent,
    CobaltLoadingIndicatorCircularComponent,
    CobaltSideNavigationComponent,
    CobaltSideNavigationItemComponent,
    HeaderComponent,
    LoadingCircularPanelComponent,
    NgFor,
    NgIf,
    RouterOutlet,
    RouterLink
  ]
})
export class AppComponent implements OnInit {
  hasClickedOnQualtric: boolean = false;
  subscription: Subscription;
  isAuthenticated: boolean;
  isOnline: boolean;
  isSessionExpired: boolean;

  private _destroyRef = inject(DestroyRef);

  constructor(
    private _analyticService: AnalyticService,
    private _authB2cService: AuthB2CService,
    private _bannerService: BannerService,
    private _cobaltIconRegistry: CobaltIconRegistry,
    private _connectionService: ConnectionService,
    private _cookieService: CookieService,
    private _featureService: FeatureService,
    private _headerService: HeaderService,
    private _heapService: HeapService,
    private _modalService: CobaltModalService,
    private _monitorService: MonitorService,
    private _propertyBagService: PropertyBagService,
    private _qualtricsService: QualtricsService,
    private _router: Router,
    private _serverErrorConnectorService: ServerErrorConnectorService,
    private _trainingEventsService: TrainingEventsService
  ) {
    this._cobaltIconRegistry.registerIcons([
      cobaltIconFeedbackOutline,
      cobaltIconHelpCircleOutline,
      cobaltIconHomeOutline,
      cobaltIconHomeSolid,
      cobaltIconLibraryOutline,
      cobaltIconLibrarySolid,
      cobaltIconQrCodeSolid,
      cobaltIconQrCodeOutline
    ]);

    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.checkRouterEvent(event);
      }
    });
  }

  public menuItems: Array<{
    iconName?: string;
    label: string;
    routerLink: string;
  }> = [
      {
        iconName: 'home',
        label: `Training`,
        routerLink: './training'
      },
      {
        iconName: 'library',
        label: `Library`,
        routerLink: './library'
      },
      {
        iconName: 'qr-code',
        label: `Access Pass`,
        routerLink: './access-pass'
      }
    ];

  public isActive(routerLink: string): boolean {
    return this._router.url.includes(routerLink.slice(2));
  }

  public async ngOnInit(): Promise<void> {
    this._setUpStorageListener();
    this._analyticService.getUserAnalytic();
    this.isAuthenticated = await this._authB2cService.isUserLoggedIn();
    this._bannerService.flush();
    this.verifyCookieQualtrics();

    this._trainingEventsService.trainingEvents$
      .pipe(
        filter((events) => events?.length > 0),
        map(
          (events) =>
            events[0].orderProgramType?.toLowerCase() ===
            ProgramType.BATTCM.toLowerCase() ||
            events[0].orderProgramType?.toLowerCase() ===
            ProgramType.BAT.toLowerCase()
        ),
        takeUntilDestroyed(this._destroyRef),
        take(1)
      )
      .subscribe((isBat) => {
        this._propertyBagService.patchIsBat(isBat);
      });

    // Banner feature flags
    combineLatest([
      this._featureService.features$.pipe(filter((features) => !!features)),
      this._propertyBagService.isBat$.pipe(
        filter((isBat) => isBat != null),
        take(1)
      )
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([features, isBat]) => {
        const featureTogglePilotWebMaintenanceCatFto = features.find(
          (f) =>
            f.name === ToggleFeatureKeys.PilotWebAppSystemNotificationCatFto
        );

        if (!isBat && featureTogglePilotWebMaintenanceCatFto?.isActive) {
          this._bannerService.showMessage(
            Message.create(
              featureTogglePilotWebMaintenanceCatFto?.value?.message,
              'informative',
              true,
              true
            )
          );
        }

        const featureTogglePilotWebMaintenanceBat = features.find(
          (f) => f.name === ToggleFeatureKeys.PilotWebAppSystemNotificationBat
        );

        if (isBat && featureTogglePilotWebMaintenanceBat?.isActive) {
          this._bannerService.showMessage(
            Message.create(
              featureTogglePilotWebMaintenanceBat?.value?.message,
              'informative',
              true,
              true
            )
          );
        }

        const featureTogglePilotWebGlobalNotification = features.find(
          (f) => f.name === ToggleFeatureKeys.PilotWebGlobalNotification
        );

        if (featureTogglePilotWebGlobalNotification?.isActive) {
          this._bannerService.showMessage(
            Message.create(
              featureTogglePilotWebGlobalNotification?.value?.message,
              featureTogglePilotWebGlobalNotification?.value?.variant,
              true,
              true
            )
          );
        }
      });

    this.isOnline = this._connectionService.isOnline;

    this._connectionService.connectionChanged$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((connectionStatus) => {
        this.isOnline = connectionStatus;
        if (!this.isOnline) {
          this._serverErrorConnectorService.setSessionExpiration(false);
          this.openNetworkConnectionDialog();
        }
      });

    this._serverErrorConnectorService.isSessionExpired$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isSessionExpired: boolean) => {
        if (isSessionExpired) this.openSessionExpirationDialog();
      });

    this._initHeap();
  }

  helpClicked(): void {
    this._headerService.showHelp();
  }

  satisfactionSurveyClicked(): void {
    this._qualtricsService.resetAllCctSurveyCookies();
    this._cookieService.set('isBtnSatisfactionSurvey', '1');
    this.hasClickedOnQualtric = true;
    this._qualtricsService.load();
  }

  verifyCookieQualtrics(): void {
    this.subscription = interval(1000).subscribe(() => {
      const hasPoppedUp = this._cookieService.get('HasPoppedUp');
      const time = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/-/g, '/')
        .replace('T', ' ');

      if (hasPoppedUp == '1') {
        this._authB2cService.tryGetClaim('userId').then((userId: string) => {
          const alert = this.hasClickedOnQualtric
            ? 'web_QualtricsSurveyManyallyRequested'
            : 'web_QualtricsSurveyRouterTriggeredAlert';

          this._monitorService.logEvent(
            {
              name: alert,
              properties: {
                time: time,
                sfContactId: userId
              }
            },
            { sendToAppInsight: false, sendToHeap: true }
          );
        });
        this._cookieService.set('HasPoppedUp', '0');
      }
    });
  }

  checkRouterEvent(routerEvent: RouterEvent): void {
    if (routerEvent instanceof NavigationEnd) {
      this._qualtricsService.resetAllCctSurveyCookies();
      this._qualtricsService.load();
    }
  }

  public openNetworkConnectionDialog(): void {
    this._modalService.open(NetworkConnectionDialogComponent, {
      size: 'small'
    });
  }

  public openSessionExpirationDialog(): void {
    this._modalService.open(SessionExperationDialogComponent, {
      size: 'medium'
    });
  }

  private _initHeap(): void {
    this._heapService
      .initializeHeap()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  private _setUpStorageListener(): void {
    window.addEventListener(
      'storage',
      (event) => {
        if (event.storageArea == localStorage) {
          if (event.key === 'cct-web-logout-evt') {
            location.reload();
            this._authB2cService.logout();
          }
        }
      },
      false
    );
  }
}
